/**
 * The documentation content model.
 *
 * Two audiences share one source of truth on purpose. A customer reads a page
 * to learn how a feature works; the team reads the same page's `status` to see
 * whether that feature is actually finished. Keeping them together is what
 * stops the docs from describing a product that does not exist — the audit
 * checklist is DERIVED from these statuses rather than maintained beside them,
 * so a page cannot claim one thing while the checklist claims another.
 */

/** How far along one layer of a feature is. */
export type Check = 'yes' | 'no' | 'partial' | 'n/a';

/** Headline state, shown as a badge next to the page title. */
export type FeatureState =
  /** Works end to end today. */
  | 'shipped'
  /** Usable, but something named in `gaps` is missing or unwired. */
  | 'partial'
  /** Designed or half-built, but a user cannot complete it. */
  | 'missing';

/**
 * The per-feature audit row.
 *
 * These are the exact questions asked of every feature during development.
 * `gaps` is the important field: a claim of `partial` with no gaps listed is
 * itself a documentation bug.
 */
export interface ImplementationStatus {
  state: FeatureState;
  frontend: Check;
  backend: Check;
  /** Reads and writes reach Firestore rather than living in process memory. */
  persistence: Check;
  /** The route is behind authentication appropriate to its audience. */
  auth: Check;
  /** Role and tenant scoping is enforced server-side, not just hidden in the UI. */
  permissions: Check;
  /** Failures surface as a typed error the caller can act on. */
  errorHandling: Check;
  /** Automated coverage exists for the behaviour, not merely the happy path. */
  tests: Check;
  /** Precisely what is not finished. Required whenever state is not 'shipped'. */
  gaps?: string[];
}

// ---- Content blocks ---------------------------------------------------------

export type Block =
  | { kind: 'text'; body: string }
  | { kind: 'heading'; body: string }
  /** Ordered instructions. Each entry is one thing the reader does. */
  | { kind: 'steps'; items: string[] }
  | { kind: 'list'; items: string[] }
  | { kind: 'table'; headers: string[]; rows: string[][] }
  | { kind: 'code'; language: string; body: string }
  /** Something worth stopping for — a constraint, a security boundary. */
  | { kind: 'note'; tone: 'info' | 'warn' | 'danger'; body: string }
  /** Explicitly unfinished. Rendered as a visible gap, never as prose. */
  | { kind: 'todo'; body: string }
  /** Error code → cause → what the reader does about it. */
  | { kind: 'errors'; rows: { code: string; cause: string; fix: string }[] }
  /**
   * Where a screenshot belongs. Rendered as a labelled placeholder rather than
   * a broken image, so a missing asset is obvious instead of silent.
   */
  | { kind: 'screenshot'; caption: string };

export interface DocPage {
  slug: string;
  title: string;
  /** One sentence, shown under the title and used by search. */
  summary: string;
  section: SectionId;
  /** Absent on conceptual pages that describe no single feature. */
  status?: ImplementationStatus;
  blocks: Block[];
  /** Extra search terms that do not appear in the prose. */
  keywords?: string[];
}

export type SectionId =
  | 'start'
  | 'concepts'
  | 'features'
  | 'roles'
  | 'integrations'
  | 'operate'
  | 'reference';

export interface Section {
  id: SectionId;
  title: string;
  /** Sidebar ordering. */
  order: number;
}

export const SECTIONS: Section[] = [
  { id: 'start', title: 'Getting started', order: 1 },
  { id: 'concepts', title: 'Core concepts', order: 2 },
  { id: 'features', title: 'Features', order: 3 },
  { id: 'roles', title: 'Roles & permissions', order: 4 },
  { id: 'integrations', title: 'Integrations & API', order: 5 },
  { id: 'operate', title: 'Running an agency', order: 6 },
  { id: 'reference', title: 'Reference', order: 7 },
];
